import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { OrderDto } from '../dto';
import { fetchOrderDetail } from '../api/ordersApi';

interface UseOrderDetailResult {
  order: OrderDto | null;
  setOrder: Dispatch<SetStateAction<OrderDto | null>>;
  loading: boolean;
  error: unknown;
  refresh: () => Promise<OrderDto>;
}

export function useOrderDetail(orderId?: string): UseOrderDetailResult {
  const [order, setOrder] = useState<OrderDto | null>(null);
  const [loading, setLoading] = useState(Boolean(orderId));
  const [error, setError] = useState<unknown>(null);

  const refresh = async () => {
    if (!orderId) {
      throw new Error('Order id is required');
    }

    setLoading(true);
    setError(null);

    try {
      const nextOrder = await fetchOrderDetail(orderId, 'order.detail');
      setOrder(nextOrder);
      return nextOrder;
    } catch (nextError) {
      setError(nextError);
      throw nextError;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isActive = true;

    if (!orderId) {
      setOrder(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const nextOrder = await fetchOrderDetail(orderId, 'order.detail');
        if (isActive) {
          setOrder(nextOrder);
        }
      } catch (nextError) {
        if (isActive) {
          setError(nextError);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [orderId]);

  return {
    order,
    setOrder,
    loading,
    error,
    refresh,
  };
}
